/**
 * Cart state — localStorage-backed, exposed via React Context.
 *
 * Shape per cart line:
 *   { item_id, name, price, thumbnail_url, custom_shipping_rate, quantity }
 *
 * Public surface (useCart hook):
 *   - items, count, subtotal, shippingEstimate, totalEstimate
 *   - addItem(item, qty=1), removeItem(item_id), updateQuantity(item_id, qty)
 *   - clear()
 *   - isOpen, openCart(), closeCart(), toggleCart()
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import logger from "./logger";

const STORAGE_KEY = "demolition_cart_v1";
const BASE_SHIPPING = 5.99; // matches backend BASE_SHIPPING_RATE
const MAX_QTY = 99;

// localStorage is fine for cart state: contains only product ids, names, quantities,
// thumbnail URLs, and prices for client-side display. No PII, no auth tokens, no
// payment data. Stripe handles all sensitive checkout/payment flows server-side.
const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (li) => li && typeof li.item_id === "string" && typeof li.quantity === "number"
    ).map((li) => ({
      // Normalize: older saved carts may not have variation_id; coerce to null.
      variation_id: null,
      variation_name: null,
      sku: null,
      max_quantity: 99,
      ...li,
    }));
  } catch (err) {
    logger.warn("Cart: failed to read from localStorage, resetting", err);
    return [];
  }
};

const writeStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    // Quota exceeded or disabled (private browsing) — recoverable, just log.
    logger.warn("Cart: failed to write to localStorage", err);
  }
};

const clampQty = (q) => {
  const n = Number.isFinite(q) ? Math.floor(q) : 1;
  if (n < 1) return 1;
  if (n > MAX_QTY) return MAX_QTY;
  return n;
};

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readStorage());
  const [isOpen, setIsOpen] = useState(false);

  // Sync to localStorage whenever items change
  useEffect(() => {
    writeStorage(items);
  }, [items]);

  // Cross-tab sync: if another tab updates the cart, mirror it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setItems(readStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem = useCallback((item, qty = 1, variation = null) => {
    if (!item || !item.id) return;
    // If product has variations, a variation must be provided
    const effectivePrice =
      variation && variation.price != null ? variation.price : item.price;
    if (effectivePrice == null) return; // can't add an affiliate / unpriced item
    const variation_id = variation?.id || null;
    const variation_name = variation?.name || null;
    const sku = variation?.sku || null;
    const shipping =
      variation && variation.custom_shipping_rate != null
        ? variation.custom_shipping_rate
        : item.custom_shipping_rate;

    const quantity = clampQty(qty);
    setItems((prev) => {
      const existingIdx = prev.findIndex(
        (li) => li.item_id === item.id && (li.variation_id || null) === variation_id
      );
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx],
          quantity: clampQty(next[existingIdx].quantity + quantity),
        };
        return next;
      }
      return [
        ...prev,
        {
          item_id: item.id,
          variation_id,
          variation_name,
          sku,
          name: item.name,
          price: Number(effectivePrice),
          thumbnail_url:
            (item.images && item.images[0]) || item.thumbnail_url || null,
          custom_shipping_rate: shipping != null ? Number(shipping) : null,
          quantity,
          // Snapshot of inventory at add-time, used to cap the in-cart qty stepper.
          max_quantity: variation ? Number(variation.inventory) : 99,
        },
      ];
    });
    // Drawer is intentionally NOT auto-opened so users can continue browsing.
  }, []);

  const removeItem = useCallback((item_id, variation_id = null) => {
    setItems((prev) =>
      prev.filter((li) => !(li.item_id === item_id && (li.variation_id || null) === variation_id))
    );
  }, []);

  const updateQuantity = useCallback((item_id, qty, variation_id = null) => {
    setItems((prev) =>
      prev.map((li) =>
        li.item_id === item_id && (li.variation_id || null) === variation_id
          ? { ...li, quantity: clampQty(qty) }
          : li
      )
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((v) => !v), []);

  const getCartQty = useCallback(
    (item_id, variation_id = null) => {
      const line = items.find(
        (li) => li.item_id === item_id && (li.variation_id || null) === variation_id
      );
      return line ? line.quantity : 0;
    },
    [items]
  );

  const derived = useMemo(() => {
    const count = items.reduce((sum, li) => sum + li.quantity, 0);
    const subtotal = items.reduce((sum, li) => sum + li.price * li.quantity, 0);
    const maxOverride = items.reduce(
      (max, li) => Math.max(max, Number(li.custom_shipping_rate) || 0),
      0
    );
    const shippingEstimate = items.length === 0 ? 0 : Math.max(BASE_SHIPPING, maxOverride);
    const totalEstimate = subtotal + shippingEstimate;
    return { count, subtotal, shippingEstimate, totalEstimate };
  }, [items]);

  // Action refs (identity stable across renders — useCallback with fixed deps).
  const actions = useMemo(
    () => ({ addItem, removeItem, updateQuantity, clear, openCart, closeCart, toggleCart }),
    [addItem, removeItem, updateQuantity, clear, openCart, closeCart, toggleCart]
  );

  // Read-heavy state (recomputes when cart contents or open flag change).
  const value = useMemo(
    () => ({
      items,
      ...derived,
      ...actions,
      getCartQty,
      isOpen,
    }),
    [items, derived, actions, getCartQty, isOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a <CartProvider>");
  }
  return ctx;
}

export const CART_BASE_SHIPPING = BASE_SHIPPING;
